import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { Input } from "@/components/ui/input";

import { getOtherCostMonthServed, type Entity, type ServedChannel } from "./api";
import { COPY, messageFor, money, TIE_FLOOR, tieMonth } from "./format";
import { channelTitle, servedChannels, servedLineLabel, servedReason } from "./servedTieModel";

const ENTITIES: readonly Entity[] = ["LTD", "OU", "LLC"];
const CHANNEL_TOTAL_NOTE = "Summed over the channels, Shown equals Cost History for the month in the company currency (OU's Amazon.se and Amazon.pl rows appear in SEK and PLN on Cost History). Per channel it can differ: the Other fees and subscriptions account is spread onto Amazon and Shopify rows but counted here under Other.";

function Amount({ value, currency, dp }: { value: string | null; currency: string; dp?: number }) {
  return <td className="py-1 pl-4 text-right tabular-nums">{money(value, currency, dp)}</td>;
}

function ChannelTieTable({ channel, currency }: { channel: ServedChannel; currency: string }) {
  return <table className="w-full text-sm">
    <caption className="pb-2 text-left font-medium">{channelTitle(channel.channel)}</caption>
    <tbody>
      <tr><th scope="row" className="py-1 text-left font-normal">Xero P&amp;L (live)</th><Amount value={channel.xero_live} currency={currency} /></tr>
      <tr><th scope="row" className="py-1 text-left font-normal">Shown in Cost History</th><Amount value={channel.shown} currency={currency} /></tr>
      {channel.lines.map((line, index) => <tr key={`${line.kind}-${index}`}><th scope="row" className="py-1 text-left font-normal">{servedLineLabel(line, channel.channel)}</th><Amount value={line.amount} currency={currency} /></tr>)}
      <tr><th scope="row" className="py-1 text-left font-semibold">Unexplained</th><Amount value={channel.unexplained} currency={currency} dp={4} /></tr>
      <tr><td colSpan={2} className="pt-2 font-semibold">{channel.ties ? "Ties to the P&L" : `Does not tie: ${money(channel.unexplained, currency, 4)} unexplained`}</td></tr>
    </tbody>
  </table>;
}

function HistoryTieCompany({ entity, month }: { entity: Entity; month: string }) {
  const query = useQuery({
    queryKey: ["pfc", "other-costs", "served", entity, `${month}-01`],
    queryFn: () => getOtherCostMonthServed(entity, `${month}-01`),
  });
  const data = query.data?.data;
  if (query.isPending) return <table className="w-full text-sm"><caption className="pb-2 text-left font-semibold">{entity}</caption><tbody><tr><td>Loading…</td></tr></tbody></table>;
  if (query.error) return <table className="w-full text-sm"><caption className="pb-2 text-left font-semibold">{entity}</caption><tbody><tr><td role="alert">{messageFor(query.error)}</td></tr></tbody></table>;
  if (!data) return null;

  const channels = servedChannels(data);
  const reason = servedReason(data);
  if (reason !== null) return <div className="flex flex-col gap-2">
    <table className="w-full text-sm">
      <caption className="pb-2 text-left font-semibold">{entity} ({data.currency})</caption>
      <tbody><tr><td colSpan={2} className="pb-1">{reason}</td></tr>{channels.map((channel) => <tr key={channel.channel}><th scope="row" className="py-1 text-left font-normal">{channelTitle(channel.channel)}</th><Amount value={channel.shown} currency={data.currency} /></tr>)}</tbody>
    </table>
    {data.xero_reloading && <p className="text-sm text-ks-grey-1">Xero is still reloading this month, so these figures can still move.</p>}
    {channels.map((channel) => channel.typed_only.length > 0 && <p key={channel.channel} className="text-sm text-ks-grey-1">{channelTitle(channel.channel)} — Not re-checked against live Xero (typed in the close): {channel.typed_only.join(", ")}</p>)}
    <p className="text-sm text-ks-grey-1">{CHANNEL_TOTAL_NOTE}</p>
  </div>;

  return <section className="flex flex-col gap-3">
    <h3 className="font-semibold">{entity} ({data.currency})</h3>
    {channels.map((channel) => <div key={channel.channel} className="flex flex-col gap-2"><ChannelTieTable channel={channel} currency={data.currency} />{channel.typed_only.length > 0 && <p className="text-sm text-ks-grey-1">Not re-checked against live Xero (typed in the close): {channel.typed_only.join(", ")}</p>}</div>)}
    {data.xero_reloading && <p className="text-sm text-ks-grey-1">Xero is still reloading this month, so these figures can still move.</p>}
    <p className="text-sm text-ks-grey-1">{CHANNEL_TOTAL_NOTE}</p>
  </section>;
}

export function HistoryTieSection({ thisMonth }: { thisMonth: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const month = tieMonth(search.h_tie_month, thisMonth);
  const max = tieMonth(undefined, thisMonth);
  return <section className="flex flex-col gap-4">
    <p className="text-sm text-ks-grey-1">{COPY.tieHeader}</p>
    <label className="flex max-w-xs flex-col text-sm">Month<Input data-nonmutating type="month" min={TIE_FLOOR} max={max} value={month} onChange={(event) => void navigate({ to: ".", search: { ...search, h_tie_month: event.target.value }, replace: true })} /></label>
    {ENTITIES.map((entity) => <HistoryTieCompany key={entity} entity={entity} month={month} />)}
  </section>;
}
